import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { api } from '@/lib/api';
import { useSession } from '@/app/session';
import { AuthLayout } from './AuthLayout';
import { useAuthForm } from './useAuthForm';
import { Button } from '@/ui/Button';
import { Field, TextInput } from '@/ui/Field';
import { Notice } from '@/ui/Panel';

export function Login({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useSession();
  const { busy, formError, fieldError, submit } = useAuthForm();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const from = (location.state as { from?: string } | null)?.from;

  return (
    <AuthLayout
      locale={locale}
      title={t('auth.loginTitle')}
      lead={t('auth.loginLead')}
      footer={
        <>
          <span className="text-ink-muted">{t('auth.noAccount')}</span>{' '}
          <Link to={localePath(locale, '/signup')} className="font-medium underline decoration-line-strong underline-offset-2 hover:decoration-ink">
            {t('auth.signUp')}
          </Link>
        </>
      }
    >
      <form
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          const ok = await submit(async () => {
            await api.post('/auth/login', { email, password });
            await refresh();
          });
          if (ok) navigate(from ?? localePath(locale, '/'), { replace: true });
        }}
      >
        {formError ? (
          <div className="mb-4">
            <Notice tone="risk">{formError}</Notice>
          </div>
        ) : null}

        <Field label={t('auth.email')} error={fieldError('email')}>
          {(p) => (
            <TextInput
              {...p}
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              autoFocus
            />
          )}
        </Field>

        <Field label={t('auth.password')} error={fieldError('password')}>
          {(p) => (
            <TextInput
              {...p}
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          )}
        </Field>

        <Button type="submit" variant="primary" busy={busy} className="mt-2 w-full">
          {t('auth.signIn')}
        </Button>

        <p className="mt-4 text-center">
          <Link
            to={localePath(locale, '/forgot-password')}
            className="text-meta text-ink-muted underline decoration-line-strong underline-offset-2 hover:text-ink hover:decoration-ink"
          >
            {t('auth.forgot')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
